'use client'

export type CityClimate = {
  spring_high: number | null
  spring_low: number | null
  summer_high: number | null
  summer_low: number | null
  fall_high: number | null
  fall_low: number | null
  winter_high: number | null
  winter_low: number | null
  spring_weather: string | null
  summer_weather: string | null
  fall_weather: string | null
  winter_weather: string | null
  avg_annual_rain_inches: number | null
  avg_annual_snow_inches: number | null
}

type SeasonData = {
  label: string
  abbr: string
  high: number | null
  low: number | null
  weather: string | null
  color: string
}

const weatherIcon = (type: string | null): string => {
  switch (type) {
    case 'sunny': return '☀️'
    case 'cloudy': return '☁️'
    case 'rainy': return '🌧️'
    case 'snowy': return '❄️'
    case 'mixed': return '⛅'
    default: return '⛅'
  }
}

export default function WeatherStrip({ climate }: { climate: CityClimate }) {
  const seasons: SeasonData[] = [
    { label: 'Spring', abbr: 'SPR', high: climate.spring_high, low: climate.spring_low, weather: climate.spring_weather, color: 'text-emerald-500' },
    { label: 'Summer', abbr: 'SUM', high: climate.summer_high, low: climate.summer_low, weather: climate.summer_weather, color: 'text-teal-500' },
    { label: 'Fall', abbr: 'FALL', high: climate.fall_high, low: climate.fall_low, weather: climate.fall_weather, color: 'text-orange-500' },
    { label: 'Winter', abbr: 'WIN', high: climate.winter_high, low: climate.winter_low, weather: climate.winter_weather, color: 'text-blue-400' },
  ]

  const hasAnyData = seasons.some((s) => s.high != null || s.low != null)
  if (!hasAnyData) return null

  return (
    <div className="flex items-center gap-1 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] px-3 py-2.5 overflow-x-auto">
      {seasons.map((season, i) => (
        <div key={season.abbr} className="flex items-center">
          {i > 0 && <span className="mx-1.5 text-[var(--border)] select-none">│</span>}
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-base" title={season.weather ?? 'mixed'}>{weatherIcon(season.weather)}</span>
            <span className={`text-xs font-bold ${season.color}`}>{season.abbr}</span>
            <span className="text-sm font-semibold text-[var(--text-1)]">
              {season.high ?? '—'}°
            </span>
            <span className="text-xs text-[var(--text-3)]">/</span>
            <span className="text-sm text-[var(--text-3)]">
              {season.low ?? '—'}°
            </span>
          </div>
        </div>
      ))}
      {(climate.avg_annual_rain_inches != null || climate.avg_annual_snow_inches != null) && (
        <>
          <span className="mx-1.5 text-[var(--border)] select-none">│</span>
          <div className="flex items-center gap-2 whitespace-nowrap text-xs text-[var(--text-3)]">
            {climate.avg_annual_rain_inches != null && (
              <span title="Annual rainfall">🌧️ {climate.avg_annual_rain_inches}"</span>
            )}
            {climate.avg_annual_snow_inches != null && climate.avg_annual_snow_inches > 0 && (
              <span title="Annual snowfall">❄️ {climate.avg_annual_snow_inches}"</span>
            )}
          </div>
        </>
      )}
    </div>
  )
}
